import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  fetchPublicProposal, acceptPublicProposal, rejectPublicProposal,
} from "../services/publicProposalService";

/**
 * Where a client answers a proposal.
 *
 * Opened from a link in their email, so there is no session, no sidebar and no
 * CMS chrome — just the offer and two answers. Everything a signed-in screen
 * takes for granted has to be spelled out here instead, because whoever lands
 * on it has no other context.
 */

const shell = {
  minHeight: "100vh", background: "#F5F6FC", display: "flex", alignItems: "center",
  justifyContent: "center", padding: 24,
  fontFamily: "'Inter',system-ui,-apple-system,sans-serif", color: "#0A0D14",
};
const cardStyle = {
  background: "#fff", borderRadius: 20, border: "1px solid #E7E9F5", width: "100%",
  maxWidth: 480, overflow: "hidden", boxShadow: "0 24px 60px -34px rgba(20,18,70,.4)",
};
const bodyStyle = { padding: "28px 30px 30px" };
const rowStyle = {
  display: "flex", justifyContent: "space-between", gap: 16, padding: "11px 0",
  borderTop: "1px solid #F0F1F8", fontSize: 14,
};
const btn = (bg, color, border = bg) => ({
  flex: 1, padding: "13px 18px", borderRadius: 11, border: `1px solid ${border}`,
  background: bg, color, fontSize: 14, fontWeight: 700, cursor: "pointer",
  fontFamily: "inherit",
});

const inr = (n) =>
  n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

/* What each closed state should say, so a client who clicks an old link is told
   what happened rather than shown a broken page. */
const CLOSED = {
  accepted: { title: "Already accepted", body: "This proposal has been accepted. Your quotation was emailed to you." },
  rejected: { title: "Already declined", body: "This proposal was declined. Get in touch if that was a mistake." },
  expired: { title: "This proposal has expired", body: "Prices change, so quotes do not stay open forever. Ask your contact for an updated one." },
};

const AcceptProposal = () => {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    let live = true;
    fetchPublicProposal(token)
      .then((data) => { if (live) setState({ status: "ready", data }); })
      .catch((err) => { if (live) setState({ status: "error", error: err.message }); });
    return () => { live = false; };
  }, [token]);

  const answer = (kind) => {
    setBusy(kind);
    const call = kind === "accept" ? acceptPublicProposal : rejectPublicProposal;
    call(token)
      .then((data) => setState({ status: kind === "accept" ? "accepted" : "rejected", data }))
      .catch((err) => setState((prev) => ({ ...prev, error: err.message })))
      .finally(() => setBusy(""));
  };

  const Frame = ({ tint, title, children }) => (
    <div style={shell}>
      <div style={cardStyle}>
        <div style={{ background: tint || "linear-gradient(160deg,#241C5A,#14123A)", color: "#fff", padding: "26px 30px" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".08em", textTransform: "uppercase", color: "#C4B5FD" }}>
            Proposal
          </div>
          <h1 style={{ margin: "8px 0 0", fontSize: 21, fontWeight: 800, color: "#fff" }}>{title}</h1>
        </div>
        <div style={bodyStyle}>{children}</div>
      </div>
    </div>
  );

  if (state.status === "loading") {
    return <Frame title="Opening your proposal…"><p style={{ color: "#3F4F65", fontSize: 14 }}>One moment.</p></Frame>;
  }

  if (state.status === "error") {
    return (
      <Frame tint="linear-gradient(160deg,#4C1D24,#2A1016)" title="This link did not open">
        <p style={{ color: "#3F4F65", fontSize: 14, lineHeight: 1.7, margin: 0 }}>{state.error}</p>
      </Frame>
    );
  }

  if (state.status === "accepted") {
    const link = state.data?.payment_link;
    return (
      <Frame tint="linear-gradient(160deg,#064E3B,#022C22)" title="Accepted — thank you">
        <p style={{ color: "#3F4F65", fontSize: 14, lineHeight: 1.7, marginTop: 0 }}>
          Your quotation has been emailed to you. You can pay whenever you are ready —
          the link below works from the email too.
        </p>
        {link ? (
          <a
            href={link}
            style={{ ...btn("linear-gradient(90deg,#5046E5,#7C3AED)", "#fff", "transparent"), display: "block", textAlign: "center", textDecoration: "none", marginTop: 8 }}
          >
            Pay now
          </a>
        ) : (
          <p style={{ color: "#8896A8", fontSize: 13 }}>
            Your contact will send the payment link shortly.
          </p>
        )}
      </Frame>
    );
  }

  if (state.status === "rejected") {
    return (
      <Frame tint="linear-gradient(160deg,#3F3F46,#18181B)" title="Declined">
        <p style={{ color: "#3F4F65", fontSize: 14, lineHeight: 1.7, margin: 0 }}>
          Thank you for letting us know. If something in the pricing did not work,
          reply to the email — we would rather hear it than guess.
        </p>
      </Frame>
    );
  }

  const d = state.data || {};
  /* A link can be opened long after it was sent; the server's own reading of the
     state is what decides, not the date on the page. */
  const closed = d.expired ? CLOSED.expired : CLOSED[d.effective_status];
  if (closed) {
    return (
      <Frame tint="linear-gradient(160deg,#3F3F46,#18181B)" title={closed.title}>
        <p style={{ color: "#3F4F65", fontSize: 14, lineHeight: 1.7, margin: 0 }}>{closed.body}</p>
      </Frame>
    );
  }

  return (
    <Frame title={d.client_name ? `Your proposal, ${d.client_name}` : "Your proposal"}>
      <div style={{ marginBottom: 22 }}>
        {d.plan_name && (
          <div style={{ ...rowStyle, borderTop: "none" }}>
            <span style={{ color: "#3F4F65" }}>Plan</span>
            <b>{d.plan_name}</b>
          </div>
        )}
        {d.amount != null && (
          <div style={rowStyle}>
            <span style={{ color: "#3F4F65" }}>First invoice</span>
            <b style={{ fontVariantNumeric: "tabular-nums" }}>{inr(d.amount)}</b>
          </div>
        )}
      </div>

      <p style={{ color: "#3F4F65", fontSize: 13.5, lineHeight: 1.7, marginTop: 0 }}>
        Accepting emails you a quotation and a payment link. Nothing is charged until
        you pay.
      </p>

      <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
        <button
          onClick={() => answer("reject")}
          disabled={Boolean(busy)}
          style={{ ...btn("#fff", "#3F4F65", "#E7E9F5"), flex: "0 0 auto", padding: "13px 20px" }}
        >
          {busy === "reject" ? "…" : "Decline"}
        </button>
        <button
          onClick={() => answer("accept")}
          disabled={Boolean(busy)}
          style={btn("linear-gradient(90deg,#5046E5,#7C3AED)", "#fff", "transparent")}
        >
          {busy === "accept" ? "Accepting…" : "Accept proposal"}
        </button>
      </div>

      {state.error && (
        <p style={{ color: "#DC2626", fontSize: 13, marginTop: 16, marginBottom: 0 }}>{state.error}</p>
      )}
    </Frame>
  );
};

export default AcceptProposal;
